import React, { useState } from 'react';

const HotelFacilities = ({ facilities = null }) => {
  const [showCompleteFacilities, setShowCompleteFacilities] = useState(false);

  // If no facilities data is provided, show a placeholder
  if (!facilities || Object.keys(facilities).length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Hotel Amenities & Facilities</h2>
          <p className="text-gray-600">Facility information will be displayed when available from the hotel.</p>
        </div>
      </div>
    );
  }

  const getIcon = (item) => {
    const iconMap = {
      'wifi': '📶',
      'internet': '📶',
      'air conditioning': '❄️',
      'elevator': '🛗',
      'housekeeping': '🧹',
      'food': '🍽️',
      'delivery': '🍽️',
      'cleaning': '👔',
      'balcony': '🏞️',
      'terrace': '🏞️',
      'accessible': '♿',
      'baby': '👶',
      'fireplace': '🔥',
      'gift': '🎁',
      'shop': '🎁',
      'parking': '🚗',
      'shuttle': '✈️',
      'taxi': '🚕',
      'bicycle': '🚲',
      'security': '🔒',
      'cctv': '📹',
      'key': '🔑',
      'fire': '🚨',
      'first aid': '🏥',
      'laundry': '👔',
      'currency': '💱',
      'atm': '🏧',
      'luggage': '📦'
    };

    const itemLower = item.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (itemLower.includes(key)) {
        return icon;
      }
    }
    return '✓';
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Amenities and Facilities</h2>
        {Object.keys(facilities).length > 0 && (
          <button
            onClick={() => setShowCompleteFacilities(!showCompleteFacilities)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
          >
            {showCompleteFacilities ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Show Less
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                View All Facilities
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Basic Facilities View */}
      {!showCompleteFacilities && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(facilities).map(([category, items], index) => {
            if (!items || (Array.isArray(items) && items.length === 0)) return null;
            
            const categoryColors = [
              'bg-blue-50 border-blue-100',
              'bg-green-50 border-green-100', 
              'bg-orange-50 border-orange-100',
              'bg-purple-50 border-purple-100',
              'bg-pink-50 border-pink-100',
              'bg-indigo-50 border-indigo-100'
            ];
            
            const iconColors = [
              'text-blue-500',
              'text-green-500',
              'text-orange-500', 
              'text-purple-500',
              'text-pink-500',
              'text-indigo-500'
            ];

            return (
              <div key={category} className={`${categoryColors[index % categoryColors.length]} rounded-xl p-4 border`}>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center capitalize">
                  <svg className={`w-4 h-4 ${iconColors[index % iconColors.length]} mr-2`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </h3>
                <ul className="space-y-2 text-sm">
                  {Array.isArray(items) ? (
                    items.slice(0, 3).map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center text-gray-700">
                        <span className={`${iconColors[index % iconColors.length]} mr-2`}>
                          {typeof item === 'object' && item.name ? getIcon(item.name) : getIcon(item)}
                        </span>
                        {typeof item === 'object' && item.name ? item.name : item}
                      </li>
                    ))
                  ) : (
                    <li className="flex items-center text-gray-700">
                      <span className={`${iconColors[index % iconColors.length]} mr-2`}>
                        {getIcon(items)}
                      </span>
                      {items}
                    </li>
                  )}
                  {Array.isArray(items) && items.length > 3 && (
                    <li className="text-xs text-gray-500 italic">
                      +{items.length - 3} more items
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Complete Facilities View */}
      {showCompleteFacilities && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(facilities).map(([category, items], index) => {
              if (!items || (Array.isArray(items) && items.length === 0)) return null;

              const categoryColors = [
                'bg-blue-50 border-blue-100',
                'bg-green-50 border-green-100', 
                'bg-orange-50 border-orange-100',
                'bg-purple-50 border-purple-100',
                'bg-pink-50 border-pink-100',
                'bg-indigo-50 border-indigo-100'
              ];
              
              const iconColors = [
                'text-blue-500',
                'text-green-500',
                'text-orange-500', 
                'text-purple-500',
                'text-pink-500',
                'text-indigo-500'
              ];

              return (
                <div key={category} className={`${categoryColors[index % categoryColors.length]} rounded-xl p-6 border`}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center capitalize">
                    <svg className={`w-5 h-5 ${iconColors[index % iconColors.length]} mr-2`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </h3>
                  <ul className="space-y-2">
                    {Array.isArray(items) ? (
                      items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start text-gray-700">
                          <span className={`${iconColors[index % iconColors.length]} mr-2 mt-0.5`}>
                            {typeof item === 'object' && item.name ? getIcon(item.name) : getIcon(item)}
                          </span>
                          <span className="text-sm">
                            {typeof item === 'object' && item.name ? item.name : item}
                          </span>
                        </li>
                      ))
                    ) : (
                      <li className="flex items-start text-gray-700">
                        <span className={`${iconColors[index % iconColors.length]} mr-2 mt-0.5`}>
                          {getIcon(items)}
                        </span>
                        <span className="text-sm">{items}</span>
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelFacilities;